// PresentationSheet.jsx — printable product sheets for store visits.
// 2026-08-21 (Brian): 9 cards per printed page (3×3, hard page breaks); per-product promo
//   pricing for new-store pitches — % off, sale price, or N+M free; one "effective until"
//   date printed small on the bottom of EVERY page.
// 2026-11-04 (Brian, from his old T&T "New Product Information" Excel sheet): a second
//   "Table" layout — label column on the left (Item Picture / Item No. / Description / Pack /
//   Price / Price per Unit / Promotion / Promo Price per Unit / Storage / Barcode), products
//   across, 4×2 per page by default (4×3 and 3×3 selectable), portrait. Every row can be
//   ticked on or off (the same toggles drive the Cards layout). Names print English /
//   Korean / both, split from the bilingual QB name by utils/productName.js. Price/Unit =
//   case price ÷ units in the pack (parsed from the name, editable). Promo Price/Unit for an
//   N+M deal = unit price × N ÷ (N+M); for % off / sale price = sale case price ÷ units.
//   EVERY printed cell is click-to-edit: print-only overrides — the product records, the
//   catalog and QuickBooks never change. Edits to price / pack flow into the unit prices;
//   a typed unit price wins over the calculation until its ↺ is clicked. Layout, name mode
//   and field choices persist per device (localStorage); edits and promos live only for the
//   session, like before.
import { Fragment, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getAllProducts } from "../services/dataCache";
import { TOFU_ORDER } from "../utils/orderSort";
import { productMatches } from "../utils/searchMatch";
import { parseProductName, unitsFromPack, nameLines } from "../utils/productName";

// Tofu family prints as one group in its canonical order (10001 ... 10098,
// 40891 last) even though 40891's number is far away — the SHEET follows
// the same tofu-first rule as invoices/packing sheets (Brian, 2026-08-21).
// Missing tofus just drop out; 40891 follows whichever tofus are selected.
const TOFU_INDEX = new Map(TOFU_ORDER.map((sku, i) => [sku, i]));

function sheetOrder(a, b) {
  const ta = TOFU_INDEX.has(a.sku) ? TOFU_INDEX.get(a.sku) : null;
  const tb = TOFU_INDEX.has(b.sku) ? TOFU_INDEX.get(b.sku) : null;
  if (ta !== null && tb !== null) return ta - tb;
  if (ta !== null) return -1;
  if (tb !== null) return 1;
  return skuCompare(a, b);
}

function skuCompare(a, b) {
  const numA = parseInt(a.sku, 10);
  const numB = parseInt(b.sku, 10);
  const aIsNum = !isNaN(numA) && /^\d+/.test(a.sku);
  const bIsNum = !isNaN(numB) && /^\d+/.test(b.sku);
  if (aIsNum && bIsNum) return numA - numB;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.sku.localeCompare(b.sku);
}

function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function formatSheetDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
}

const CARDS_PER_PAGE = 9; // cards layout: 3 columns × 3 rows per printed page (settled 2026-08-21)
const DENSITIES = {
  "4x2": { cols: 4, rows: 2, label: "4 × 2 (8 per page)" },
  "4x3": { cols: 4, rows: 3, label: "4 × 3 (12 per page, small pictures)" },
  "3x3": { cols: 3, rows: 3, label: "3 × 3 (9 per page)" },
};
// Printed rows, in print order. Brian ticks them on/off; barcode is off by default.
const FIELD_DEFS = [
  ["picture", "Item Picture"],
  ["sku", "Item No."],
  ["description", "Description"],
  ["pack", "Pack"],
  ["price", "Price (CAD)"],
  ["unitPrice", "Price / Unit"],
  ["promo", "Promotion"],
  ["promoUnit", "Promo Price / Unit"],
  ["storage", "Storage"],
  ["barcode", "Barcode"],
];
const PRICE_FIELDS = new Set(["price", "unitPrice", "promoUnit"]); // hidden by "Hide prices"
const DEFAULT_FIELDS = Object.fromEntries(FIELD_DEFS.map(([k]) => [k, k !== "barcode"]));
const DEFAULT_PREFS = { layout: "table", density: "4x2", nameMode: "both", fields: DEFAULT_FIELDS };
const PREFS_KEY = "presentationPrefs.v1";
const DEFAULT_TITLE = "New Product Information";
// QB brand prefixes → the name printed on the Brand line (unknown codes print as-is).
const BRAND_NAMES = { PMO: "Pulmuone", HT: "Haitai", DW: "Dongwon", "동원": "Dongwon", "삼립": "Samlip", SLP: "Samlip" };

function loadPrefs() {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
    return {
      ...DEFAULT_PREFS,
      ...saved,
      layout: saved.layout === "cards" ? "cards" : "table",
      density: DENSITIES[saved.density] ? saved.density : "4x2",
      nameMode: ["both", "en", "ko"].includes(saved.nameMode) ? saved.nameMode : "both",
      fields: { ...DEFAULT_FIELDS, ...(saved.fields || {}) },
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

const money = (v) => `$${Number(v).toFixed(2)}`;
function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

// Everything one product prints, with the promo and Brian's print-only edits applied.
// `ov` = overrides for this SKU: { sku, en, ko, pack, price, unitPrice, promoUnit, storage, barcode }.
function resolveLine(p, promo, ov, nameMode) {
  const parsed = parseProductName(p.name);
  const en = ov.en ?? parsed.en;
  const ko = ov.ko ?? parsed.ko;
  const pack = ov.pack ?? parsed.pack;
  const units = ov.pack != null ? unitsFromPack(ov.pack) : parsed.units;
  const price = ov.price ?? p.basePrice;
  const unitPrice = ov.unitPrice ?? (units > 0 && price != null ? price / units : null);

  let promoLabel = "";
  let promoUnit = null;
  if (promo?.type === "nm") {
    const n = parseInt(promo.n, 10);
    const m = parseInt(promo.m, 10);
    if (n > 0 && m > 0) {
      promoLabel = `${n}+${m} FREE`;
      // Brian's formula (2026-11-04): what one unit really costs when every N+M come for the price of N.
      if (unitPrice != null) promoUnit = (unitPrice * n) / (n + m);
    }
  } else if (promo?.type === "pct") {
    const pct = num(promo.pct);
    if (pct > 0 && pct < 100) {
      const sale = price * (1 - pct / 100);
      promoLabel = `${pct}% off → ${money(sale)}/cs`;
      if (units > 0) promoUnit = sale / units;
    }
  } else if (promo?.type === "price") {
    const v = num(promo.price);
    if (v > 0) {
      promoLabel = `Sale ${money(v)}/cs`;
      if (units > 0) promoUnit = v / units;
    }
  }
  if (ov.promoUnit != null) promoUnit = ov.promoUnit;

  let lines = nameLines({ en, ko }, nameMode);
  if (lines.length === 0) lines = [p.name];
  return {
    skuText: ov.sku ?? p.sku,
    en, ko, lines, pack, units, price, unitPrice, promoLabel, promoUnit,
    storage: ov.storage ?? (p.storageMethod || ""),
    barcode: ov.barcode ?? (p.barcode || ""),
  };
}

// Click-to-edit text. Prints exactly what is shown; the input only exists while editing.
// Empty text (or an unreadable number) clears the override → back to the product/calculated value.
function Editable({ value, display, numeric, edited, placeholder, onCommit, onReset, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function start(e) {
    e.stopPropagation();
    setText(value == null || value === "" ? "" : numeric ? Number(value).toFixed(2) : String(value));
    setEditing(true);
  }

  function finish(commit) {
    setEditing(false);
    if (!commit) return;
    if (numeric) onCommit(num(text.replace(/[^0-9.-]/g, "")));
    else onCommit(text.trim() === "" ? null : text.trim());
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={`ed-input ${className}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); finish(true); }
          if (e.key === "Escape") finish(false);
        }}
      />
    );
  }
  const empty = value == null || value === "";
  return (
    <span className={`ed-wrap ${className}`}>
      <span
        className={`ed ${edited ? "ed-set" : ""} ${empty ? "ed-empty" : ""}`}
        title="Click to edit — changes only this printout"
        onClick={start}
      >
        {empty ? (placeholder || "—") : (display ?? value)}
      </span>
      {edited && onReset && (
        <button
          type="button"
          className="ed-reset no-print"
          title="Back to the original value"
          onClick={(e) => { e.stopPropagation(); onReset(); }}
        >↺</button>
      )}
    </span>
  );
}

export default function PresentationSheet() {
  const { profile } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [selected, setSelected] = useState({}); // sku -> true
  const [promos, setPromos] = useState({});     // sku -> { type, pct, price, n, m }
  const [overrides, setOverrides] = useState({}); // sku -> { field: value } (print-only edits)

  const [customerName, setCustomerName] = useState("");
  const [sheetDate, setSheetDate] = useState(todayISO());
  const [repName, setRepName] = useState(profile?.name || "");
  const [sheetTitle, setSheetTitle] = useState(DEFAULT_TITLE);
  const [brandLine, setBrandLine] = useState(""); // blank = automatic (single brand selected)
  const [hidePrices, setHidePrices] = useState(false);
  const [promoUntil, setPromoUntil] = useState(""); // printed on every page
  const [prefs, setPrefs] = useState(loadPrefs);

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* private mode etc. */ }
  }, [prefs]);

  useEffect(() => {
    async function load() {
      const all = await getAllProducts(); // session cache — 0 reads on revisit
      setProducts(all.filter(p => p.active).sort(skuCompare));
      setLoading(false);
    }
    load();
  }, []);

  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();

  const filtered = products.filter(p => {
    const matchesSearch = productMatches(p, search); // punctuation-blind
    const matchesBrand = brandFilter === "all" || p.brand === brandFilter;
    return matchesSearch && matchesBrand;
  });

  const selectedProducts = products.filter(p => selected[p.sku]).sort(sheetOrder);

  const isTable = prefs.layout === "table";
  const { cols, rows } = DENSITIES[prefs.density] || DENSITIES["4x2"];
  const perPage = isTable ? cols * rows : CARDS_PER_PAGE;
  const pages = [];
  for (let i = 0; i < selectedProducts.length; i += perPage) {
    pages.push(selectedProducts.slice(i, i + perPage));
  }

  // Rows actually printed: ticked, and price rows drop out under "Hide prices".
  const visibleFields = FIELD_DEFS.filter(([k]) => prefs.fields[k] && !(hidePrices && PRICE_FIELDS.has(k)));

  const selectedBrands = [...new Set(selectedProducts.map(p => p.brand).filter(Boolean))];
  const autoBrand = selectedBrands.length === 1 ? (BRAND_NAMES[selectedBrands[0]] || selectedBrands[0]) : "";
  const brandShown = brandLine.trim() || autoBrand;

  const editCount = Object.values(overrides).reduce((n, o) => n + Object.keys(o).length, 0);

  function toggle(sku) {
    setSelected(prev => {
      const next = { ...prev };
      if (next[sku]) delete next[sku];
      else next[sku] = true;
      return next;
    });
  }

  // Click-and-drag selection (Brian, 2026-08-21): press on a row and sweep
  // down — every row passed takes the state the FIRST row flipped to (drag
  // over selected rows while starting on an unselected one still selects).
  // A plain click is just a one-row drag. Promo controls are excluded so
  // typing a discount never starts a drag.
  const dragSetTo = useRef(null);

  useEffect(() => {
    const end = () => { dragSetTo.current = null; };
    window.addEventListener("mouseup", end);
    return () => window.removeEventListener("mouseup", end);
  }, []);

  function setSku(sku, on) {
    setSelected(prev => {
      if (!!prev[sku] === on) return prev;
      const next = { ...prev };
      if (on) next[sku] = true;
      else delete next[sku];
      return next;
    });
  }

  function rowMouseDown(p, e) {
    if (e.button !== 0) return; // left button only
    if (e.target.closest(".pick-promo")) return; // promo editing, not selection
    e.preventDefault(); // no text-selection while sweeping
    const setTo = !selected[p.sku];
    dragSetTo.current = setTo;
    setSku(p.sku, setTo);
  }

  function rowMouseEnter(p) {
    if (dragSetTo.current === null) return;
    setSku(p.sku, dragSetTo.current);
  }

  function setPromo(sku, patch) {
    setPromos(prev => {
      const cur = prev[sku] || {};
      const next = { ...cur, ...patch };
      if (!next.type) {
        const rest = { ...prev };
        delete rest[sku];
        return rest;
      }
      return { ...prev, [sku]: next };
    });
  }

  // Print-only edit of one printed value. null clears it (back to the product / calculation).
  function setOverride(sku, field, value) {
    setOverrides(prev => {
      const cur = { ...(prev[sku] || {}) };
      if (value == null) delete cur[field];
      else cur[field] = value;
      const next = { ...prev };
      if (Object.keys(cur).length) next[sku] = cur;
      else delete next[sku];
      return next;
    });
  }

  function setField(key, on) {
    setPrefs(prev => ({ ...prev, fields: { ...prev.fields, [key]: on } }));
  }

  // Selects everything currently SHOWN (respects search + brand filter),
  // merging into any existing selection.
  function selectAllFiltered() {
    setSelected(prev => {
      const next = { ...prev };
      filtered.forEach(p => { next[p.sku] = true; });
      return next;
    });
  }

  // One editable printed value for product p. `ov` = its overrides, `r` = resolved line.
  function editable(p, ov, field, value, extra = {}) {
    return (
      <Editable
        value={value}
        edited={ov[field] != null}
        onCommit={(v) => setOverride(p.sku, field, v)}
        onReset={() => setOverride(p.sku, field, null)}
        {...extra}
      />
    );
  }

  function descriptionCells(p, ov, r) {
    if (prefs.nameMode === "both") {
      return (
        <>
          <div className="sheet-name-en">{editable(p, ov, "en", r.en, { placeholder: "English name" })}</div>
          <div className="sheet-name-ko">{editable(p, ov, "ko", r.ko, { placeholder: "한글 이름" })}</div>
        </>
      );
    }
    const field = prefs.nameMode === "en" ? "en" : "ko";
    const shown = prefs.nameMode === "en" ? (r.en || r.ko) : (r.ko || r.en);
    return <div className="sheet-name-en">{editable(p, ov, field, shown, { placeholder: "Name" })}</div>;
  }

  function cellFor(key, p, ov, r) {
    switch (key) {
      case "picture":
        return p.imageUrl
          ? <img src={p.imageUrl} alt="" />
          : <div className="tbl-noimage">No photo</div>;
      case "sku":
        return <strong>{editable(p, ov, "sku", r.skuText)}</strong>;
      case "description":
        return descriptionCells(p, ov, r);
      case "pack":
        return editable(p, ov, "pack", r.pack, { placeholder: "e.g. 12 x 500g" });
      case "price":
        return editable(p, ov, "price", r.price, { numeric: true, display: money(r.price) });
      case "unitPrice":
        return editable(p, ov, "unitPrice", r.unitPrice, {
          numeric: true,
          display: r.unitPrice != null ? money(r.unitPrice) : null,
          placeholder: r.units ? "—" : "— (set Pack)",
        });
      case "promo":
        return r.promoLabel ? <span className="sheet-red">{r.promoLabel}</span> : null;
      case "promoUnit":
        return (
          <span className="sheet-red sheet-strong">
            {editable(p, ov, "promoUnit", r.promoUnit, {
              numeric: true,
              display: r.promoUnit != null ? money(r.promoUnit) : null,
              placeholder: "—",
            })}
          </span>
        );
      case "storage":
        return editable(p, ov, "storage", r.storage);
      case "barcode":
        return editable(p, ov, "barcode", r.barcode);
      default:
        return null;
    }
  }

  function sheetHeader() {
    return (
      <div className="sheet-header">
        <div className="sheet-header-left">
          <h1>Sunjin Trading Inc.</h1>
          <p className="sheet-addr">
            #119, 1647 Broadway St., Port Coquitlam, B.C., Canada<br />
            Office 604-468-0456 · Fax 604-468-0466
          </p>
        </div>
        <div className="sheet-header-right">
          {sheetTitle.trim() && <h2 className="sheet-title">{sheetTitle}</h2>}
          <p>
            <strong>Date:</strong> {formatSheetDate(sheetDate)}
            {customerName && <> · <strong>Prepared for:</strong> {customerName}</>}
          </p>
          {(brandShown || repName) && (
            <p>
              {brandShown && <><strong>Brand:</strong> {brandShown}</>}
              {brandShown && repName && " · "}
              {repName && <><strong>Rep:</strong> {repName}</>}
            </p>
          )}
        </div>
      </div>
    );
  }

  function tablePage(chunk, pi) {
    const blocks = [];
    for (let i = 0; i < chunk.length; i += cols) blocks.push(chunk.slice(i, i + cols));
    return (
      <div key={pi} className={`sheet-page tbl-page tbl-${prefs.density} ${pi === 0 ? "sheet-page-first" : "sheet-page-rest"}`}>
        {pi === 0 && sheetHeader()}
        <div className="tbl-blocks">
          {blocks.map((items, bi) => (
            <div
              key={bi}
              className={`tbl-block ${prefs.fields.picture ? "has-pic" : ""}`}
              style={{ gridTemplateColumns: `var(--tbl-label-w) repeat(${cols}, minmax(0, 1fr))` }}
            >
              {visibleFields.map(([key, label]) => (
                <Fragment key={key}>
                  <div className={`tbl-label tbl-label-${key}`}>{label}</div>
                  {items.map(p => {
                    const ov = overrides[p.sku] || {};
                    const r = resolveLine(p, promos[p.sku], ov, prefs.nameMode);
                    return <div key={p.sku} className={`tbl-cell tbl-cell-${key}`}>{cellFor(key, p, ov, r)}</div>;
                  })}
                  {Array.from({ length: cols - items.length }).map((_, i) => (
                    <div key={`empty-${i}`} className={`tbl-cell tbl-cell-${key} tbl-cell-empty`} />
                  ))}
                </Fragment>
              ))}
            </div>
          ))}
        </div>
        {promoUntil && (
          <p className="sheet-page-footer">
            * Promotional pricing effective until {formatSheetDate(promoUntil)}
          </p>
        )}
      </div>
    );
  }

  function cardsPage(chunk, pi) {
    const f = prefs.fields;
    return (
      <div key={pi} className={`sheet-page cards-page ${pi === 0 ? "sheet-page-first" : "sheet-page-rest"}`}>
        {pi === 0 && sheetHeader()}
        <div className="sheet-grid">
          {chunk.map(p => {
            const ov = overrides[p.sku] || {};
            const r = resolveLine(p, promos[p.sku], ov, prefs.nameMode);
            const showPrice = f.price && !hidePrices;
            const showUnit = f.unitPrice && !hidePrices && r.unitPrice != null;
            const showPromoUnit = f.promoUnit && !hidePrices && r.promoUnit != null;
            return (
              <div key={p.sku} className="sheet-card">
                {f.picture && (p.imageUrl
                  ? <img src={p.imageUrl} alt={p.name} />
                  : <div className="sheet-card-noimage">No photo</div>)}
                {f.sku && <p className="sheet-card-sku">#{editable(p, ov, "sku", r.skuText)}</p>}
                {f.barcode && <p className="sheet-card-barcode">Barcode: {editable(p, ov, "barcode", r.barcode)}</p>}
                {f.description && <div className="sheet-card-name">{descriptionCells(p, ov, r)}</div>}
                {f.pack && <p className="sheet-card-pack">{editable(p, ov, "pack", r.pack, { placeholder: "e.g. 12 x 500g" })}</p>}
                {showPrice && (
                  <p className="sheet-card-price">
                    {editable(p, ov, "price", r.price, { numeric: true, display: money(r.price) })} CAD
                  </p>
                )}
                {showUnit && (
                  <p className="sheet-card-unit">
                    {editable(p, ov, "unitPrice", r.unitPrice, { numeric: true, display: money(r.unitPrice) })} / unit
                  </p>
                )}
                {f.promo && r.promoLabel && <p className="sheet-card-promo sheet-red">{r.promoLabel}</p>}
                {showPromoUnit && (
                  <p className="sheet-card-promo-unit sheet-red">
                    {editable(p, ov, "promoUnit", r.promoUnit, { numeric: true, display: money(r.promoUnit) })} / unit
                  </p>
                )}
                {f.storage && <p className="sheet-card-storage">{editable(p, ov, "storage", r.storage)}</p>}
              </div>
            );
          })}
        </div>
        {/* Date set = footer prints on every page, full stop (an
            earlier and-a-promo-exists condition made it vanish). */}
        {promoUntil && (
          <p className="sheet-page-footer">
            * Promotional pricing effective until {formatSheetDate(promoUntil)}
          </p>
        )}
      </div>
    );
  }

  if (loading) return <p>Loading products...</p>;

  return (
    <div className="presentation-page">
      <div className="presentation-builder no-print">
        <h2>Presentation Sheet</h2>

        <div className="sheet-header-inputs">
          <label>
            Customer name
            <input
              placeholder="e.g. H-Mart Coquitlam"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </label>
          <label>
            Date
            <input
              type="date"
              value={sheetDate}
              onChange={(e) => setSheetDate(e.target.value)}
            />
          </label>
          <label>
            Rep name
            <input
              placeholder="Rep name"
              value={repName}
              onChange={(e) => setRepName(e.target.value)}
            />
          </label>
          <label>
            Sheet title
            <input
              placeholder={DEFAULT_TITLE}
              value={sheetTitle}
              onChange={(e) => setSheetTitle(e.target.value)}
            />
          </label>
          <label>
            Brand line
            <input
              placeholder={autoBrand ? `auto: ${autoBrand}` : "auto when one brand is selected"}
              value={brandLine}
              onChange={(e) => setBrandLine(e.target.value)}
            />
          </label>
          <label>
            Promos effective until
            <input
              type="date"
              value={promoUntil}
              onChange={(e) => setPromoUntil(e.target.value)}
              title="Prints small at the bottom of every page when set"
            />
          </label>
        </div>

        <div className="sheet-options">
          <label>
            Layout
            <select value={prefs.layout} onChange={(e) => setPrefs(prev => ({ ...prev, layout: e.target.value }))}>
              <option value="table">Table — labels on the left</option>
              <option value="cards">Cards — 3 × 3</option>
            </select>
          </label>
          {isTable && (
            <label>
              Per page
              <select value={prefs.density} onChange={(e) => setPrefs(prev => ({ ...prev, density: e.target.value }))}>
                {Object.entries(DENSITIES).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
              </select>
            </label>
          )}
          <label>
            Names
            <select value={prefs.nameMode} onChange={(e) => setPrefs(prev => ({ ...prev, nameMode: e.target.value }))}>
              <option value="both">English + Korean</option>
              <option value="en">English only</option>
              <option value="ko">Korean only</option>
            </select>
          </label>
          <div className="sheet-fields">
            <span className="sheet-fields-title">Show:</span>
            {FIELD_DEFS.map(([k, label]) => (
              <label key={k} className="sheet-field-toggle">
                <input type="checkbox" checked={!!prefs.fields[k]} onChange={(e) => setField(k, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="sheet-print-controls">
          <label className="hide-prices-toggle">
            <input
              type="checkbox"
              checked={hidePrices}
              onChange={(e) => setHidePrices(e.target.checked)}
            />
            Hide prices on this sheet
          </label>
          <span className="selected-count">Selected: {selectedProducts.length} ({pages.length || 0} page{pages.length !== 1 ? "s" : ""})</span>
          <button
            className="clear-selection-btn"
            disabled={filtered.length === 0}
            onClick={selectAllFiltered}
          >
            Select all ({filtered.length})
          </button>
          <button
            className="clear-selection-btn"
            disabled={selectedProducts.length === 0}
            onClick={() => setSelected({})}
          >
            Clear all
          </button>
          <button
            className="clear-selection-btn"
            disabled={editCount === 0}
            title="Undo every click-to-edit change on the preview"
            onClick={() => setOverrides({})}
          >
            Reset edits{editCount ? ` (${editCount})` : ""}
          </button>
          <button
            className="print-btn"
            disabled={selectedProducts.length === 0}
            onClick={() => window.print()}
          >
            🖨 Print (Ctrl+P)
          </button>
        </div>

        <div className="presentation-controls">
          <input
            placeholder="Search by name, brand, or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
            <option value="all">All Brands</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>

        <div className="presentation-pick-list">
          {filtered.map(p => {
            const promo = promos[p.sku];
            const sale = promo?.type === "pct" && num(promo.pct) > 0 && num(promo.pct) < 100
              ? p.basePrice * (1 - num(promo.pct) / 100)
              : null;
            return (
              <div
                key={p.sku}
                className="pick-row"
                onMouseDown={(e) => rowMouseDown(p, e)}
                onMouseEnter={() => rowMouseEnter(p)}
              >
                <input
                  type="checkbox"
                  checked={!!selected[p.sku]}
                  readOnly
                  style={{ pointerEvents: "none" }}
                />
                {p.imageUrl
                  ? <img src={p.imageUrl} alt="" className="pick-thumb" loading="lazy" />
                  : <span className="pick-thumb pick-thumb-empty" />}
                <span className="pick-sku">{p.sku}</span>
                <span className="pick-name">{p.name}</span>
                {/* Promo controls live on every row, left of the regular
                    price (Brian, 2026-08-21). Picking a promo type on an
                    unselected row also selects it — a promo you configure
                    is a promo you mean to print. */}
                <span className="pick-promo" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={promo?.type || ""}
                    onChange={(e) => {
                      setPromo(p.sku, { type: e.target.value });
                      if (e.target.value && !selected[p.sku]) toggle(p.sku);
                    }}
                  >
                    <option value="">No promo</option>
                    <option value="pct">% off</option>
                    <option value="price">Sale price $</option>
                    <option value="nm">N + M free</option>
                  </select>
                  {promo?.type === "pct" && (
                    <span className="pick-promo-inputs">
                      <input
                        type="number" min="1" max="99" placeholder="10"
                        value={promo.pct ?? ""}
                        onChange={(e) => setPromo(p.sku, { pct: e.target.value })}
                      />%
                      {sale != null && <em>→ ${sale.toFixed(2)}</em>}
                    </span>
                  )}
                  {promo?.type === "price" && (
                    <span className="pick-promo-inputs">
                      $<input
                        type="number" min="0.01" step="0.01" placeholder={p.basePrice.toFixed(2)}
                        value={promo.price ?? ""}
                        onChange={(e) => setPromo(p.sku, { price: e.target.value })}
                      />
                      {num(promo.price) != null && num(promo.price) >= p.basePrice && <em className="error">≥ regular!</em>}
                    </span>
                  )}
                  {promo?.type === "nm" && (
                    <span className="pick-promo-inputs">
                      <input
                        type="number" min="1" placeholder="10"
                        value={promo.n ?? ""}
                        onChange={(e) => setPromo(p.sku, { n: e.target.value })}
                      />+<input
                        type="number" min="1" placeholder="1"
                        value={promo.m ?? ""}
                        onChange={(e) => setPromo(p.sku, { m: e.target.value })}
                      /> free
                    </span>
                  )}
                </span>
                <span className="pick-price">${p.basePrice.toFixed(2)}</span>
              </div>
            );
          })}
          {filtered.length === 0 && <p className="pick-empty">No products match your search.</p>}
        </div>

        <h3 className="preview-heading">
          Print preview — this is exactly what prints. Click any value to change it for this printout only.
        </h3>
      </div>

      <div className={`sheet ${isTable ? "sheet-table" : "sheet-cards"}`}>
        {selectedProducts.length === 0 ? (
          <p className="sheet-empty no-print">Select products above to build the sheet.</p>
        ) : (
          pages.map((chunk, pi) => (isTable ? tablePage(chunk, pi) : cardsPage(chunk, pi)))
        )}
      </div>
    </div>
  );
}
